import { useTranslation } from "react-i18next"

import type { FairUseImagePriceRow, FairUseImagePricesIssue } from "./fair-use-pricing-utils"

import { Plus, Trash2 } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

import { fairUseImagePriceRow } from "./fair-use-pricing-utils"

type FairUseImagePricesProps = {
  rows: FairUseImagePriceRow[]
  /** Mirrors `PLATFORM_ONLY_CONFIG_FIELDS`: tenants read, the platform writes. */
  canEdit: boolean
  issue: FairUseImagePricesIssue
  onChange: (rows: FairUseImagePriceRow[]) => void
}

/** The shared key, accepted at model, size AND quality level. */
const DEFAULT_KEY = "default"

/** The three parts of one price, so the header and the inputs cannot drift. */
const KEY_FIELDS = [
  { field: "model", labelKey: "modelLabel", fallback: "Image model", width: "w-52" },
  { field: "size", labelKey: "sizeLabel", fallback: "Size", width: "w-36" },
  { field: "quality", labelKey: "qualityLabel", fallback: "Quality", width: "w-28" },
] as const

/**
 * What a generated image costs (#3089, spec R5).
 *
 * Priced on three keys because that is how the provider prices images: the
 * same deployment costs several times more at 1536x1024/high than at
 * 1024x1024/low, so one rate per model would either over-charge every cheap
 * image or under-charge every expensive one. `default` is accepted at each
 * level, so `default / default / default` is a complete, legitimate table —
 * one price for every image the tenant makes.
 *
 * A model with no price meters a real 0.00 SEK row and logs once; it is never
 * refused, so an empty table is an unbilled state rather than an error. What
 * LIMITS images is the `images_per_month` entitlement in the matrix above —
 * this table only decides what they cost.
 *
 * Keys are stored lower-cased and the backend refuses two that differ only in
 * case with a 400, so that is caught here rather than left to be a coin flip
 * over which price wins.
 *
 * Platform-only, for the obvious reason: the price is an input to the invoice.
 */
export function FairUseImagePrices({ rows, canEdit, issue, onChange }: FairUseImagePricesProps) {
  const { t } = useTranslation()

  const patch = (index: number, next: Partial<FairUseImagePriceRow>) => {
    onChange(rows.map((row, position) => (position === index ? { ...row, ...next } : row)))
  }

  const header = (
    <div className="space-y-1">
      <Label className="text-sm font-semibold">
        {t("adminDashboard.fairUse.settings.imagePrices.title", "Image price")}
      </Label>
      <p className="text-muted-foreground max-w-prose text-xs">
        {t(
          "adminDashboard.fairUse.settings.imagePrices.caption",
          "SEK per generated image, per model, size and quality. Use “default” at any of the three for one price covering the rest; an image with no price is metered at 0 SEK."
        )}
      </p>
    </div>
  )

  if (!canEdit) {
    return (
      <div className="space-y-2" data-testid="fair-use-image-prices-readonly">
        {header}
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {t("adminDashboard.fairUse.settings.imagePrices.none", "No image price is set.")}
          </p>
        ) : (
          <ul className="space-y-1 text-sm">
            {rows.map((row) => (
              <li key={row.key} className="flex items-baseline gap-2">
                <span className="font-mono text-xs">
                  {row.model} · {row.size} · {row.quality}
                </span>
                <span className="tabular-nums">
                  {t("adminDashboard.fairUse.settings.imagePrices.perImageValue", {
                    defaultValue: "{{price}} SEK / image",
                    price: row.price,
                  })}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="fair-use-image-prices">
      {header}

      {rows.length === 0 ? (
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.fairUse.settings.imagePrices.emptyHint",
            "No price yet — images are metered at 0 SEK until one is added."
          )}
        </p>
      ) : null}

      <div className="space-y-2">
        {rows.map((row, index) => (
          <div key={row.key} className="flex flex-wrap items-end gap-2">
            {KEY_FIELDS.map(({ field, labelKey, fallback, width }) => (
              <div key={field} className="space-y-1">
                <Label className="text-xs" htmlFor={`fair-use-image-${field}-${index}`}>
                  {t(`adminDashboard.fairUse.settings.imagePrices.${labelKey}`, fallback)}
                </Label>
                <Input
                  className={`h-9 ${width} font-mono text-xs`}
                  id={`fair-use-image-${field}-${index}`}
                  placeholder={DEFAULT_KEY}
                  value={row[field]}
                  onChange={(event) => patch(index, { [field]: event.target.value })}
                />
              </div>
            ))}
            <div className="space-y-1">
              <Label className="text-xs" htmlFor={`fair-use-image-price-${index}`}>
                {t("adminDashboard.fairUse.settings.imagePrices.priceLabel", "SEK / image")}
              </Label>
              <Input
                className="h-9 w-28 tabular-nums"
                id={`fair-use-image-price-${index}`}
                inputMode="decimal"
                value={row.price}
                onChange={(event) => patch(index, { price: event.target.value })}
              />
            </div>
            <Button
              className="h-9"
              size="icon"
              type="button"
              variant="ghost"
              aria-label={t("adminDashboard.fairUse.settings.imagePrices.remove", {
                defaultValue: "Remove the price for {{model}}",
                model: row.model || DEFAULT_KEY,
              })}
              onClick={() => onChange(rows.filter((_row, position) => position !== index))}
            >
              <Trash2 className="text-destructive size-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        size="sm"
        type="button"
        variant="outline"
        onClick={() => onChange([...rows, fairUseImagePriceRow()])}
      >
        <Plus className="mr-2 size-4" />
        {t("adminDashboard.fairUse.settings.imagePrices.add", "Add a price")}
      </Button>

      {issue === "key" && (
        <p className="text-destructive text-xs" data-testid="fair-use-image-key-error">
          {t(
            "adminDashboard.fairUse.settings.imagePrices.keyError",
            "Every price needs a model, a size and a quality — use “default” where one price covers the rest."
          )}
        </p>
      )}
      {issue === "duplicate" && (
        <p className="text-destructive text-xs" data-testid="fair-use-image-duplicate-error">
          {t(
            "adminDashboard.fairUse.settings.imagePrices.duplicateError",
            "Two prices name the same model, size and quality. They are compared without case."
          )}
        </p>
      )}
      {issue === "range" && (
        <p className="text-destructive text-xs" data-testid="fair-use-image-range-error">
          {t(
            "adminDashboard.fairUse.settings.imagePrices.rangeError",
            "A price is 0 SEK or more, at most 1000, written with a comma or a point (1,50) and no thousands grouping."
          )}
        </p>
      )}
    </div>
  )
}
